import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { useEffect, useRef } from 'react';
import { blogApi } from '@/features/blogs/api/blog.api';

interface BlogRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
}

export function BlogRichTextEditor({ value, onChange }: BlogRichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || '<p></p>',
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // TipTap only uses `content` on init — sync when parent hydrates saved HTML
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const next = value || '<p></p>';
    if (next === editor.getHTML()) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  const insertImage = async (file: File) => {
    if (!editor) return;
    try {
      const uploaded = await blogApi.uploadImage(file);
      if (!uploaded?.url) return;
      editor.chain().focus().setImage({ src: uploaded.url }).run();
    } catch (err) {
      console.error('[BlogRichTextEditor] image upload failed', err);
    }
  };

  if (!editor) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#F8FAFC',
        }}
      >
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => editor.chain().focus().toggleBold().run()}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            Bold
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            sx={{ fontStyle: 'italic', textTransform: 'none' }}
          >
            Italic
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            sx={{ textTransform: 'none' }}
          >
            H2
          </Button>
          <Button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={{ textTransform: 'none' }}
          >
            List
          </Button>
        </ButtonGroup>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            const url = window.prompt('Link URL');
            if (!url) return;
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
          sx={{ textTransform: 'none' }}
        >
          Link
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none' }}
        >
          Image
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void insertImage(file);
            e.target.value = '';
          }}
        />
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          minHeight: 280,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: 240,
            '& p': { my: 1 },
            '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
            '& h2': { mt: 2, mb: 1 },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
